import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { ApiError } from '../api/client';

/**
 * Поля форм справочников: курьеры, локации, зоны, пользователи.
 *
 * Вынесены из панели курьера, когда появилась вторая форма: четыре панели с одинаковыми
 * подписями, подсказками и ошибками, написанные каждая по-своему, расходятся в первую же
 * неделю — у одной ошибка под полем, у другой тостом.
 *
 * Подпись связана с полем через `htmlFor`, ошибка и подсказка — через
 * `aria-describedby` (`{id}-error` / `{id}-hint`): экранный диктор читает их вместе с
 * полем, а не отдельной строкой где-то ниже.
 */

export function FormField({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly required?: string | undefined;
  readonly hint?: string | undefined;
  readonly error: string | null;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div style={fieldStyle}>
      <label htmlFor={id} style={labelStyle}>
        {required === undefined ? label : `${label} · ${required}`}
      </label>
      {children}
      <FieldNote id={id} hint={hint} error={error} />
    </div>
  );
}

/** Подсказка или ошибка под полем: ошибка вытесняет подсказку, место под строку одно. */
export function FieldNote({
  id,
  hint,
  error,
}: {
  readonly id: string;
  readonly hint?: string | undefined;
  readonly error: string | null;
}): ReactElement | null {
  if (error !== null) {
    return (
      <span id={`${id}-error`} style={errorStyle}>
        {error}
      </span>
    );
  }

  return hint === undefined ? null : (
    <span id={`${id}-hint`} style={hintStyle}>
      {hint}
    </span>
  );
}

/** `aria-describedby` поля: ошибка, если есть, иначе подсказка, иначе ничего. */
export function describedBy(id: string, hasError: boolean, hasHint = false): string | undefined {
  if (hasError) {
    return `${id}-error`;
  }

  return hasHint ? `${id}-hint` : undefined;
}

/** Общий блок ошибки формы: что произошло и код для поддержки (разд. 15). */
export function FormAlert({ text }: { readonly text: string | null }): ReactElement | null {
  return text === null ? null : (
    <div role="alert" style={alertStyle}>
      {text}
    </div>
  );
}

/** Код для поддержки: идентификатор запроса, если сервер его дал, иначе код ошибки. */
export function supportCode(cause: ApiError): string {
  return cause.problem.trace_id ?? `${cause.status} ${cause.code}`;
}

export const DISABLED_OPACITY = 0.6;

export const formStyle: CSSProperties = { display: 'grid', gap: 'var(--sp-4)', minWidth: 0 };
export const fieldStyle: CSSProperties = { display: 'grid', gap: 'var(--sp-1)', minWidth: 0 };
export const pairStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
  gap: 'var(--sp-3)',
};
export const labelStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 'var(--fs-caption)',
  padding: 0,
};
export const hintStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 'var(--fs-caption)',
};
export const errorStyle: CSSProperties = { color: 'var(--danger)', fontSize: 'var(--fs-caption)' };
export const fieldsetStyle: CSSProperties = {
  border: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 'var(--sp-1)',
  minWidth: 0,
};
export const checksStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-4)' };
export const checkStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--sp-2)',
  alignItems: 'center',
  minHeight: 'var(--field-h)',
};
export const alertStyle: CSSProperties = {
  color: 'var(--danger)',
  background: 'var(--danger-weak)',
  borderRadius: 'var(--r-sm)',
  padding: 'var(--sp-2) var(--sp-3)',
};
export const actionsStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-2)',
  justifyContent: 'flex-end',
};
/** Разделитель секций формы: «Основное», «Карта», «Часы работы». */
export const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--fs)',
  fontWeight: 'var(--fw-semibold)',
  margin: 0,
};
